import { useCallback, useEffect, useState, type Dispatch, type FormEvent } from "react";
import { trackAppInsightsException } from "../../../AnalyticsTracking/app-insights-telemetry";
import { useProvetStatic } from "../context";
import { toggleProvetResultView } from "../index";
import { ProvetDialogStep } from "../types";
import { ApiError } from "../utilities/apiClient";
import {
  ProvetBookingFlowAction,
  ProvetBookingFlowActionType,
  ProvetBookingFlowState
} from "./provetBookingFlowReducer";
import { TrackingState } from "./useProvetTracking";

interface UseBookingSubmissionOptions {
  bookingFlowState: ProvetBookingFlowState;
  bookingFlowDispatch: Dispatch<ProvetBookingFlowAction>;
  tracking: TrackingState;
  sessionId: string;
  modals: {
    summary: {
      close: () => void;
    };
  };
  updateBookingStep: (
    provetDialogStep: ProvetDialogStep,
    isComplete?: boolean
  ) => Promise<void>;
  setEditDialog: (
    step: ProvetDialogStep,
    isComplete?: boolean,
    transition?: boolean
  ) => Promise<void>;
  /** Called whenever the user is about to retry a submission, so the caller can refresh the reCAPTCHA token. */
  onTokenRefreshNeeded?: () => void;
}

interface BookingSubmissionHandlers {
  onFormSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  resetBooking: () => Promise<void>;
  submissionError: Error | null;
  clearSubmissionError: () => void;
}

/**
 * Hook for managing booking submission and reset.
 * Handles form validation, API submission, error state, and booking flow reset.
 */
export const useBookingSubmission = ({
  bookingFlowState,
  bookingFlowDispatch,
  tracking,
  sessionId,
  modals,
  updateBookingStep,
  setEditDialog,
  onTokenRefreshNeeded
}: UseBookingSubmissionOptions): BookingSubmissionHandlers => {
  const { api } = useProvetStatic();
  const [resetInProgress, setResetInProgress] = useState(false);
  const [submissionError, setSubmissionError] = useState<Error | null>(null);

  const onFormSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (bookingFlowState.bookingSubmitted) {
        return;
      }

      // Clear any previous errors
      setSubmissionError(null);

      // EARLY VALIDATION - Prevent API calls when data is missing
      if (!bookingFlowState.formData) {
        console.warn("Form submission blocked: No form data available");
        return;
      }

      if (!bookingFlowState.formData.selectedAppointmentTime?.availableTime) {
        console.warn("Form submission blocked: No available time selected");
        return;
      }

      if (!bookingFlowState.formData.selectedDepartment) {
        console.warn("Form submission blocked: No department selected");
        return;
      }

      if (!bookingFlowState.formData.selectedReason) {
        console.warn("Form submission blocked: No reason selected");
        return;
      }

      if (!bookingFlowState.formData.contactDetails) {
        console.warn("Form submission blocked: No contact details provided");
        return;
      }

      if (!bookingFlowState.formData.phoneVerified) {
        console.warn("Form submission blocked: Phone not verified");
        // Navigate back to contact details step (which includes phone verification)
        await setEditDialog(ProvetDialogStep.ContactDetails, false, true);
        return;
      }

      if (bookingFlowState.activeStep !== ProvetDialogStep.Confirmation) {
        console.warn("Form submission blocked: Not on confirmation step");
        return;
      }

      try {
        bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetBookingSubmitted,
          submitted: true
        });

        // Submit booking using the API
        // Email and phone number are already in contactDetails.client from phone verification
        const result = await api.submitBooking(bookingFlowState.formData);

        // Set booking ID for tracking. This sets it on the tracking service straight away,
        // so the Confirmed event below carries it.
        if (result.appointmentData?.id) {
          tracking.setBookingId(result.appointmentData.id);
        }

        // Keep the cancel token in memory only (not in tracking): it lets this browser cancel
        bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetCancelToken,
          cancelToken: result.cancelToken
        });

        // Close modal and show confirmation
        modals.summary.close();
        await updateBookingStep(ProvetDialogStep.Confirmed, true);

        // Show the consent container with booking confirmed content
        toggleProvetResultView();
      } catch (error) {
        console.error("Error submitting booking:", error);

        // Report to Application Insights (no personal data; the response body is deliberately
        // not included since it may echo user input).
        trackAppInsightsException(error, {
          Operation: "SubmitBooking",
          Flow: "Provet",
          Step: ProvetDialogStep[bookingFlowState.activeStep],
          BookingSessionId: sessionId,
          ClinicId: tracking.selectedCareUnit?.id?.toString(),
          ClinicPmsId: tracking.selectedCareUnit?.pmsId?.toString(),
          Endpoint: error instanceof ApiError ? error.endpoint : undefined,
          StatusCode: error instanceof ApiError ? error.statusCode : undefined
        });

        // Store the error so it can be displayed to the user
        setSubmissionError(error as Error);

        bookingFlowDispatch({
          type: ProvetBookingFlowActionType.SetBookingSubmitted,
          submitted: false
        });
      }
    },
    [
      bookingFlowState,
      updateBookingStep,
      modals,
      tracking,
      api,
      bookingFlowDispatch,
      sessionId
    ]
  );

  const clearSubmissionError = useCallback(() => {
    if (!submissionError) {
      return;
    }

    setSubmissionError(null);
    onTokenRefreshNeeded?.();
  }, [submissionError, onTokenRefreshNeeded]);

  const resetBooking = useCallback(async () => {
    // Clear any submission errors when resetting
    setSubmissionError(null);

    // Hide the consent container when resetting
    const consentElement = document.getElementById("c-provet-online-booking__consent");
    if (consentElement && consentElement.getAttribute("data-active") === "true") {
      toggleProvetResultView();
    }

    bookingFlowDispatch({ type: ProvetBookingFlowActionType.ResetBooking, sessionId });
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetBookingSubmitted,
      submitted: false
    });
    tracking.setBookingId(undefined);
    // Events after "book another" must not carry the previous clinic
    tracking.setSelectedCareUnit(undefined);
    tracking.setPetSpeciesName("");
    tracking.setHashedEmail("");
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.IncrementFormResetCounter
    });
    bookingFlowDispatch({
      type: ProvetBookingFlowActionType.SetActiveStep,
      step: ProvetDialogStep.SelectClinic
    });
    onTokenRefreshNeeded?.();
    setResetInProgress(true); // Set flag to wait for state update
  }, [bookingFlowDispatch, sessionId, tracking, onTokenRefreshNeeded]);

  // Synchronize side effect after state update
  useEffect(() => {
    if (
      resetInProgress &&
      bookingFlowState.activeStep === ProvetDialogStep.SelectClinic
    ) {
      console.log(
        `A reset is in progress, the current step is ${bookingFlowState.activeStep}`
      );
      setEditDialog(ProvetDialogStep.SelectClinic, false, true);
      tracking.trackingService.trackBookingEvent(ProvetDialogStep.SelectClinic, false);
      setResetInProgress(false);
    }
  }, [
    resetInProgress,
    bookingFlowState.activeStep,
    setEditDialog,
    tracking.trackingService
  ]);

  return {
    onFormSubmit,
    resetBooking,
    submissionError,
    clearSubmissionError
  };
};
